import * as React from "react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Label } from "@/components/ui/label"
import { Icon } from "@/icons/icon"

export interface ComboboxProps {
  className?: string
  label?: string
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  multiple?: boolean
  maxAllowed?: number
  lockDefaultValue?: boolean
  defaultValue?: string[]
  options: ComboboxOption[]
  disabled?: boolean

  /**
   * As a template eg: {} Products
   * {} will be replaced with selected count
   */
  selectedText?: string

  onChange?: (selected: string[]) => void
}

export interface ComboboxOption {
  label: string
  value: string
}

export default function Combobox(props: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [show, setShow] = React.useState(false)
  const buttonRef = React.useRef<HTMLButtonElement | null>(null)

  const buttonDims = useButtonSize(buttonRef)
  const [selected, setSelected] = React.useState<Set<string>>(new Set())
  const [selectedLabels, setSelectedLabels] = React.useState<Set<string>>(
    new Set()
  )

  useInit()

  function formatPlaceholder(): string {
    if (!selectedLabels.size) {
      return props.placeholder || "Select options"
    }

    if (selectedLabels.size < 2) {
      return `${selectedLabels.values().next().value}`
    }

    if (props.selectedText) {
      return props.selectedText.replace("{}", `${selectedLabels.size}`)
    }

    return `${selectedLabels.size} Selected`
  }

  function valuesToLabels(values: string[]) {
    const optionLookup: Record<string, ComboboxOption> = {}

    for (const option of props.options) {
      optionLookup[option.value] = option
    }

    return values
      .filter((item) => !!optionLookup[item])
      .map((item) => optionLookup[item].label)
  }

  function isDefaultValue(value: string) {
    return !!props.defaultValue?.find((item) => item === value)
  }

  React.useEffect(() => {
    if (props.defaultValue) {
      setSelected(new Set(props.defaultValue))
      setSelectedLabels(new Set(valuesToLabels(props.defaultValue)))
    }
  }, [props.defaultValue])

  function useButtonSize(ref: React.RefObject<HTMLButtonElement | null>) {
    const [dims, setDims] = React.useState<{
      height: string
      width: string
    }>({ height: "auto", width: "auto" })

    React.useLayoutEffect(() => {
      if (!ref.current) {
        return
      }

      const rect = ref.current.getBoundingClientRect()
      setDims({
        height: `${rect.height}px`,
        width: `${rect.width}px`,
      })
    }, [])

    return dims
  }

  function handleSelect(option: ComboboxOption) {
    if (!props.multiple) {
      const newSelected = new Set([option.value])
      setSelected(newSelected)
      setSelectedLabels(new Set([option.label]))
      setOpen(false)

      if (props.onChange) {
        props.onChange([...newSelected.values()])
      }

      return
    }

    if (!selected.has(option.value)) {
      if (
        typeof props.maxAllowed === "number" &&
        selected.size >= props.maxAllowed
      ) {
        return
      }

      const newSelected = new Set<string>(selected)
      newSelected.add(option.value)
      setSelected(newSelected)

      const newSelectedLabels = new Set(selectedLabels)
      newSelectedLabels.add(option.label)
      setSelectedLabels(newSelectedLabels)

      if (props.onChange) {
        props.onChange([...newSelected.values()])
      }
    } else {
      const newSelected = new Set(selected)
      newSelected.delete(option.value)
      setSelected(newSelected)

      const newSelectedLabels = new Set(selectedLabels)
      newSelectedLabels.delete(option.label)
      setSelectedLabels(newSelectedLabels)

      if (props.onChange) {
        props.onChange([...newSelected.values()])
      }
    }
  }

  function useInit() {
    React.useEffect(() => {
      setTimeout(() => {
        setShow(true)
      }, 500)
    }, [])
  }

  function returnKey() {
    return Math.random().toString(36).substring(2, 15)
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild disabled={props.disabled}>
        <div
          className={cn(
            "w-full flex flex-col gap-2 max-w-110",
            props.disabled && "pointer-events-none text-label-text",
            props.className
          )}
        >
          <Label>{props.label}</Label>

          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            ref={buttonRef}
            disabled={props.disabled}
            className={cn(
              "w-full h-12 rounded-sm text-sm justify-between",
              props.disabled && "pointer-events-none text-label-text disabled:bg-box-l-border"
            )}
          >
            <span
              className={cn(
                "truncate text-label-text max-w-11/12",
                selected.size && "text-button-black",
                props.disabled && "text-label-text disabled:bg-box-l-border"
              )}
            >
              {formatPlaceholder()}
            </span>
            <Icon name="ChevronUp" className="opacity-50 rotate-180" />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent className={cn("p-0")} style={{ width: buttonDims.width }}>
        <Command>
          <CommandInput
            placeholder={props.searchPlaceholder || "Type and search..."}
            className="h-12 text-base font-normal"
          />

          <CommandList>
            <CommandEmpty>
              {props.emptyMessage || "No option found."}
            </CommandEmpty>

            {show && (
              <CommandGroup className="px-0">
                {props.options.map((option) => (
                  <CommandItem
                    className="
                    text-button-black font-medium py-2.5 px-3.5 data-[selected=true]:bg-blue-fill rounded-none
                    text-base
                  "
                    key={returnKey()}
                    value={option.value}
                    keywords={[option.label]}
                    disabled={
                      props.lockDefaultValue && isDefaultValue(option.value)
                    }
                    onSelect={() => handleSelect(option)}
                  >
                    {option.label}
                    <Icon
                      name="Check"
                      className={cn(
                        "ml-auto text-blue w-5 h-auto",
                        selected.has(option.value) ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
